import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { commsService } from '../services/commsService';
import { patientService } from '../services/patientService';

const emptyTask = {
  title: '',
  description: '',
  patientId: '',
  priority: 'normal',
  taskType: 'general',
  dueAt: '',
};

const Tasks = () => {
  const [tab, setTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState(emptyTask);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [t, r, rem, p] = await Promise.all([
        commsService.listTasks(),
        commsService.listAppointmentRequests(),
        commsService.listReminders(),
        patientService.getPatients({}),
      ]);
      setTasks(t.data || []);
      setRequests(r.data || []);
      setReminders(rem.data || []);
      setPatients(Array.isArray(p) ? p : p.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patientLabel = (p) =>
    p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || '—';

  const createTask = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      setBusy(true);
      await commsService.createTask({
        ...form,
        patientId: form.patientId || undefined,
        dueAt: form.dueAt || undefined,
      });
      setForm(emptyTask);
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to create task');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await commsService.updateTask(id, { status });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const scanNotes = async () => {
    try {
      setBusy(true);
      const res = await commsService.scanMissingNotes();
      alert(`Created ${res.data?.created || 0} missing-note task(s).`);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const runReminders = async () => {
    try {
      setBusy(true);
      await commsService.runReminders();
      await load();
      alert('Reminder sweep completed.');
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const approveRequest = async (id) => {
    try {
      await commsService.updateAppointmentRequest(id, { status: 'approved' });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const declineRequest = async (id) => {
    try {
      await commsService.updateAppointmentRequest(id, { status: 'declined' });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-semibold text-primary">Tasks &amp; Reminders</h1>
              <p className="text-gray-500 text-sm mt-1">
                Practice to-dos, missing notes, portal booking requests, and appointment reminder logs
                (TheraNest-style ops on free tier).
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={scanNotes}
                disabled={busy}
                className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Scan missing notes
              </button>
              <button
                type="button"
                onClick={runReminders}
                disabled={busy}
                className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Run reminders now
              </button>
              <button
                type="button"
                onClick={() => setShowForm(!showForm)}
                className="px-3 py-2 text-sm bg-primary text-white rounded-lg"
              >
                {showForm ? 'Cancel' : 'New task'}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'tasks', label: 'Tasks' },
              { id: 'requests', label: 'Booking requests' },
              { id: 'reminders', label: 'Reminder log' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  tab === t.id ? 'bg-primary text-white' : 'bg-gray-100'
                }`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {error && <p className="text-red-600 mb-4">{error}</p>}
          {loading && <p>Loading…</p>}

          {showForm && (
            <form
              onSubmit={createTask}
              className="bg-white border rounded-xl p-4 mb-6 grid md:grid-cols-2 gap-3"
            >
              <input
                className="border rounded px-3 py-2 text-sm"
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
              <select
                className="border rounded px-3 py-2 text-sm"
                value={form.patientId}
                onChange={(e) => setForm({ ...form, patientId: e.target.value })}
              >
                <option value="">Patient (optional)</option>
                {patients.map((p) => (
                  <option key={p._id} value={p._id}>
                    {patientLabel(p)}
                  </option>
                ))}
              </select>
              <select
                className="border rounded px-3 py-2 text-sm"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
              <input
                type="datetime-local"
                className="border rounded px-3 py-2 text-sm"
                value={form.dueAt}
                onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
              />
              <textarea
                className="border rounded px-3 py-2 text-sm md:col-span-2"
                rows={2}
                placeholder="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <button
                type="submit"
                disabled={busy}
                className="bg-primary text-white px-4 py-2 rounded-lg text-sm md:col-span-2"
              >
                Create task
              </button>
            </form>
          )}

          {!loading && tab === 'tasks' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Title</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Priority</th>
                    <th className="p-3">Due</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-gray-500">
                        No tasks yet.
                      </td>
                    </tr>
                  )}
                  {tasks.map((t) => (
                    <tr key={t._id} className="border-t">
                      <td className="p-3">
                        <div className="font-medium">{t.title}</div>
                        {t.patientId && (
                          <div className="text-xs text-gray-500">
                            {patientLabel(t.patientId)}
                          </div>
                        )}
                      </td>
                      <td className="p-3">{t.taskType}</td>
                      <td className="p-3">{t.priority}</td>
                      <td className="p-3">
                        {t.dueAt ? new Date(t.dueAt).toLocaleString() : '—'}
                      </td>
                      <td className="p-3">{t.status}</td>
                      <td className="p-3 space-x-2">
                        {t.status !== 'done' && (
                          <button
                            type="button"
                            className="text-primary underline"
                            onClick={() => setStatus(t._id, 'done')}
                          >
                            Done
                          </button>
                        )}
                        {t.status === 'open' && (
                          <button
                            type="button"
                            className="text-gray-600 underline"
                            onClick={() => setStatus(t._id, 'in_progress')}
                          >
                            Start
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'requests' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Client</th>
                    <th className="p-3">Preferred time</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-gray-500">
                        No portal booking requests.
                      </td>
                    </tr>
                  )}
                  {requests.map((r) => (
                    <tr key={r._id} className="border-t">
                      <td className="p-3">
                        {r.contactName || patientLabel(r.patientId)}
                        <div className="text-xs text-gray-500">{r.contactEmail}</div>
                      </td>
                      <td className="p-3">
                        {r.preferredStartAt
                          ? new Date(r.preferredStartAt).toLocaleString()
                          : 'Flexible'}
                      </td>
                      <td className="p-3">{r.preferredType}</td>
                      <td className="p-3">{r.status}</td>
                      <td className="p-3 space-x-2">
                        {r.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              className="text-primary underline"
                              onClick={() => approveRequest(r._id)}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="text-red-600 underline"
                              onClick={() => declineRequest(r._id)}
                            >
                              Decline
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'reminders' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Channel</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">To</th>
                    <th className="p-3">When</th>
                    <th className="p-3">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {reminders.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-gray-500">
                        No reminder logs yet. Hourly cron sends 24h email reminders when SMTP/SES is
                        configured.
                      </td>
                    </tr>
                  )}
                  {reminders.map((r) => (
                    <tr key={r._id} className="border-t">
                      <td className="p-3">{r.channel}</td>
                      <td className="p-3">{r.status}</td>
                      <td className="p-3">{r.toAddress || '—'}</td>
                      <td className="p-3">
                        {r.sentAt || r.createdAt
                          ? new Date(r.sentAt || r.createdAt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="p-3 text-xs text-red-600">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Tasks;
